import React from "react";

// Read-only view of an autonomous-engine paper account (/api/paper/accounts +
// /api/positions). There are deliberately no action buttons here: the manual
// trader must never modify what the engines hold.

const num = (v, dp = 2) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const price = (v) => {
  if (v == null) return "—";
  const a = Math.abs(v);
  return num(v, a >= 1000 ? 2 : a >= 1 ? 4 : 6);
};
const usd = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `${v < 0 ? "−" : ""}$${num(Math.abs(v))}`);
const pct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const sign = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-zinc-300");
const since = (t) => {
  if (!t) return "—";
  const ms = Date.now() - new Date(String(t).replace(" ", "T") + (String(t).includes("Z") || String(t).includes("+") ? "" : "Z")).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
};

export function EngineSummaryBar({ summary, metric }) {
  const equity = summary.balance == null ? null : summary.balance + (summary.unrealized_pnl ?? 0);
  return (
    <div className="flex-1 flex items-center overflow-x-auto no-scrollbar divide-x divide-zinc-800 ml-2">
      {metric("Balance", usd(summary.balance))}
      {metric("Equity", usd(equity))}
      {metric("Total P&L", usd(summary.total_pnl), sign(summary.total_pnl))}
      {metric("Unrealized", usd(summary.unrealized_pnl), sign(summary.unrealized_pnl))}
      {metric("Margin used", usd(summary.margin_used))}
      {metric("Open", summary.open_positions ?? 0)}
      {metric("Win rate", summary.total_trades ? `${summary.win_rate}% of ${summary.total_trades}` : "—")}
    </div>
  );
}

export default function EngineAccountView({ positions, onSelectSymbol }) {
  if (!positions.length) {
    return <div className="p-6 text-center text-zinc-500">This account has no open positions right now.</div>;
  }
  const head = ["Symbol", "Side", "Qty", "Entry", "TP", "SL", "Last", "uPnL", "ROE %", "Size", "Lev", "Margin", "Engine", "Open for"];
  return (
    <table className="w-full text-left">
      <thead className="sticky top-0 bg-zinc-900 text-[10px] uppercase text-zinc-500">
        <tr>{head.map((h) => <th key={h} className="px-2 py-1.5 font-medium whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {positions.map((p) => {
          const base = (p.base || p.symbol || "").replace("/USDT", "");
          const long = p.direction === "LONG";
          const cell = "px-2 py-1.5 whitespace-nowrap font-mono tabular-nums";
          return (
            <tr key={p.id || `${p.symbol}-${p.opened_at}`} onClick={() => onSelectSymbol?.(base)}
              className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" title="Show on chart">
              <td className="px-2 py-1.5 whitespace-nowrap"><span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{base}/USDT</span></td>
              <td className={`px-2 py-1.5 ${long ? "text-emerald-400" : "text-rose-400"}`}>{long ? "Long" : "Short"}</td>
              <td className={cell}>{num(p.quantity, 4)}</td>
              <td className={cell}>{price(p.entry_price)}</td>
              <td className={cell}>{price(p.take_profit ?? p.target_price)}</td>
              <td className={cell}>{price(p.stop_loss ?? p.stop_price)}</td>
              <td className={cell}>{price(p.current_price)}</td>
              <td className={`${cell} ${sign(p.unrealized_pnl)}`}>{usd(p.unrealized_pnl)}</td>
              <td className={`${cell} ${sign(p.roe_pct ?? p.unrealized_pnl_pct)}`}>{pct(p.roe_pct ?? p.unrealized_pnl_pct)}</td>
              <td className={cell}>{usd(p.position_size_usd ?? p.position_size)}</td>
              <td className={cell}>{p.leverage != null ? `${Number(p.leverage)}x` : "—"}</td>
              <td className={cell}>{usd(p.margin)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap text-zinc-400">{p.engine || p.strategy || "—"}</td>
              <td className={cell}>{since(p.opened_at || p.entry_time)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
